import type { InvoiceData } from "@/apis/gql/types";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import type { ColumnDef } from "@tanstack/react-table";
import { endOfDay, format, isWithinInterval, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";

type PaymentDeadlineRow = {
  invoice: Pick<InvoiceData, "isPaid" | "dueDate" | "invoiceDate" | "status">;
};

const paymentDeadlineColumn = <
  T extends PaymentDeadlineRow,
>(): ColumnDef<T> => {
  return {
    id: "dueDate",
    size: 130,
    accessorKey: "invoice.dueDate",
    header: (header) => {
      const { t } = useTranslation();

      return (
        <DataTableColumnHeader
          column={header.column}
          title={t("payment_deadline")}
        />
      );
    },
    filterFn: (row, columnId, value) => {
      if (!value) return true;

      if (row.original.invoice.isPaid) {
        return false;
      }

      let dueDate = new Date(row.original.invoice.dueDate);

      if (dueDate === null || dueDate.getTime() === 0) {
        dueDate = new Date(row.original.invoice.invoiceDate);
      }

      const { to, from } = value;

      if (to && from) {
        return isWithinInterval(dueDate, {
          start: from,
          end: to,
        });
      }

      if (to) {
        return isWithinInterval(dueDate, {
          start: startOfDay(to),
          end: endOfDay(to),
        });
      }

      if (from) {
        return isWithinInterval(dueDate, {
          start: startOfDay(from),
          end: endOfDay(from),
        });
      }

      return true;
    },
    sortingFn: (a, b) => {
      const isProcessingA = a.original.invoice.status === InvoiceStatus.NEW;
      const isProcessingB = b.original.invoice.status === InvoiceStatus.NEW;

      // "isProcessing" should always be on top
      if (isProcessingA && !isProcessingB) return -1;
      if (!isProcessingA && isProcessingB) return 1;
      if (isProcessingA && isProcessingB) return 0;

      // If "isPaid", treat as if it has no deadline (sort last)
      const isPaidA = a.original.invoice.isPaid;
      const isPaidB = b.original.invoice.isPaid;

      if (isPaidA && !isPaidB) return 1;
      if (!isPaidA && isPaidB) return -1;
      if (isPaidA && isPaidB) return 0;

      const dueDateA = new Date(a.original.invoice.dueDate);
      const dueDateB = new Date(b.original.invoice.dueDate);
      const invoiceDateA = new Date(a.original.invoice.invoiceDate);
      const invoiceDateB = new Date(b.original.invoice.invoiceDate);

      const effectiveDateA =
        dueDateA && dueDateA.getTime() !== 0 ? dueDateA : invoiceDateA;
      const effectiveDateB =
        dueDateB && dueDateB.getTime() !== 0 ? dueDateB : invoiceDateB;

      return effectiveDateA.getTime() - effectiveDateB.getTime();
    },
    cell: (cell) => {
      return (() => {
        const invoice = cell.row.original.invoice;

        const isInvoicePaid = invoice?.isPaid;
        const dueDateRaw = invoice?.dueDate;
        if (!dueDateRaw || isInvoicePaid) {
          return <div className="flex justify-center w-full">–</div>;
        }
        const dueDate = new Date(dueDateRaw);
        const today = new Date();
        const dueDateDay = new Date(
          dueDate.getFullYear(),
          dueDate.getMonth(),
          dueDate.getDate(),
        );
        const todayDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        let label = null;
        if (!invoice.isPaid) {
          if (dueDateDay.getTime() < todayDay.getTime()) {
            label = <span className="text-xs text-red-600">Überfällig</span>;
          } else if (dueDateDay.getTime() === todayDay.getTime()) {
            label = <span className="text-xs text-red-600">Heute</span>;
          }
        }
        return (
          <div className="flex flex-col items-start pl-4">
            <span>{format(dueDate, "dd.MM.yy")}</span>
            {label && <span>{label}</span>}
          </div>
        );
      })();
    },
  };
};

export default paymentDeadlineColumn;
